import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Maximize, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { GapChart } from "@/components/GapChart";
import { Donut, IncomeBars } from "@/components/NetWorthDonut";
import { lifeGapRows } from "@/components/KpiStrip";
import { api } from "@/lib/api";
import { computeSynopsis, recommendedActions } from "@/lib/calc";
import { money } from "@/lib/format";
import { ASSET_ROWS } from "@/lib/schema";
import { migrate } from "@/lib/migrate";
import { applyScenario, scenarioDefaults } from "@/lib/insights";
import { Headlines, StatusCards } from "@/components/StatusCards";
import { WhatIfPanel } from "@/components/WhatIfPanel";

const SLIDES = ["At a glance", "Financial Position", "Retirement", "Protection & Plan"];
const tone = (bad) => (bad ? "text-rose-600" : "text-emerald-600");

const Card = ({ label, value, cls, sub, testId }) => (
  <div className="p-card"><span className="p-card-label">{label}</span><span className={`font-mono text-2xl font-bold ${cls || "text-slate-900"}`} data-testid={testId}>{value}</span>{sub && <span className="text-xs text-slate-500">{sub}</span>}</div>
);

const Bar = ({ label, value, max, color, m }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-sm"><span className="text-slate-600">{label}</span><span className="font-mono font-semibold">{m(value)}</span></div>
    <div className="h-3 rounded bg-slate-100"><div className="h-3 rounded" style={{ width: `${max ? Math.min(100, (value / max) * 100) : 0}%`, background: color }} /></div>
  </div>
);

const Glance = ({ s, cur }) => (
  <div className="space-y-5">
    <Headlines s={s} cur={cur} testId="present-headlines" />
    <StatusCards s={s} cur={cur} testId="present-status" />
  </div>
);

const Position = ({ s, m, cur }) => {
  const { ov, exp, d } = s;
  const max = Math.max(ov.totalAssets, ov.totalLiabilities, 1);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Household income / year" value={m(ov.annualIncome)} />
        <Card label="Monthly expenses" value={m(exp.totalMonthly)} />
        <Card label="Monthly cash flow" value={ov.cashFlow === null ? "Not entered" : m(ov.cashFlow)} cls={ov.cashFlow === null ? "text-slate-400" : tone(ov.cashFlow < 0)} testId="present-cash-flow" />
        <Card label="Net worth" value={m(ov.netWorth)} testId="present-net-worth" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="p-panel"><h3>Where your money goes</h3><Donut data={exp.byCategory.map((c) => ({ name: c.category, value: c.cur }))} currency={cur} size={220} testId="present-spending-donut" /></div>
        <div className="p-panel space-y-3">
          <h3>What you own vs. what you owe</h3>
          <Bar label="Total assets" value={ov.totalAssets} max={max} color="#0072C6" m={m} />
          <Bar label="Total liabilities" value={ov.totalLiabilities} max={max} color="#E11D48" m={m} />
          <div className="pt-2 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Investments & savings</span><span className="font-mono">{m(ov.investmentsTotal)}</span></div>
            {ASSET_ROWS.filter(([k]) => Number(d.financial.assets[k]?.amount) > 0).map(([k, l]) => <div key={k} className="flex justify-between"><span className="text-slate-600">{l}</span><span className="font-mono">{m(d.financial.assets[k].amount)}</span></div>)}
          </div>
        </div>
      </div>
    </div>
  );
};

const Retirement = ({ s, m, cur }) => {
  const r = s.ret;
  const short = r.monthlySurplus < 0;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Desired income / month" value={m(r.monthlyGoal)} sub={`from age ${r.retAge}`} />
        <Card label="Projected income / month" value={m(r.expectedMonthly)} sub={r.withSpouse ? "household" : "CPP, OAS, pension & savings"} testId="present-projected" />
        <Card label={short ? "Monthly shortfall" : "Monthly surplus"} value={m(r.monthlySurplus)} cls={tone(short)} testId="present-surplus" />
        <Card label="Additional saving needed" value={r.monthlyContribution ? `${m(r.monthlyContribution)}/mo` : "None"} cls={tone(!!r.monthlyContribution)} />
      </div>
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="p-panel lg:col-span-3">
          <h3>Your retirement income sources</h3>
          <IncomeBars sources={r.sources} goal={r.monthlyGoal} currency={cur} />
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 mt-4 text-sm">
            {r.sources.map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span className="font-mono">{m(v)}</span></div>)}
          </div>
        </div>
        <div className="p-panel lg:col-span-2 space-y-2 text-sm">
          <h3>Savings picture</h3>
          <div className="flex justify-between"><span className="text-slate-600">Savings today</span><span className="font-mono">{m(r.existingSavings)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Projected at retirement</span><span className="font-mono">{m(r.totalProjected)}</span></div>
          <div className="flex justify-between font-semibold"><span>Financial Independence Number</span><span className="font-mono" data-testid="present-fin">{m(r.fin)}</span></div>
          {r.withSpouse && <div className="pt-2 border-t border-slate-100 space-y-1">
            <div className="flex justify-between"><span className="text-slate-600">{s.d.profile.name || "Client"} income</span><span className="font-mono">{m(r.client.expected)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">{s.d.profile.spouse.name || "Spouse"} income</span><span className="font-mono">{m(r.spouse.expected)}</span></div>
          </div>}
        </div>
      </div>
    </div>
  );
};

const Protection = ({ s, m, cur }) => {
  const people = [["client", s.d.profile.name || "Client"], ...(s.ins.hasSpouse ? [["spouse", s.d.profile.spouse.name || "Spouse"]] : [])];
  return (
    <div className="space-y-5">
      {people.map(([k, name]) => {
        const r = s.ins[k];
        return (
          <div key={k} className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid={`present-protection-${k}`}>
            <Card label={`${name} · Life insurance gap`} value={r.lifeAdditional > 0 ? m(r.lifeAdditional) : "Covered"} cls={tone(r.lifeAdditional > 0)} />
            <Card label="Disability gap / month" value={r.diShortfall > 0 ? m(r.diShortfall) : "Covered"} cls={tone(r.diShortfall > 0)} />
            <Card label="Critical illness gap" value={r.ciAdditional > 0 ? m(r.ciAdditional) : "Covered"} cls={tone(r.ciAdditional > 0)} />
          </div>
        );
      })}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="p-panel"><h3>Coverage needed vs. in place</h3><GapChart rows={lifeGapRows(s)} currency={cur} height={240} /></div>
        <div className="p-panel">
          <h3>Your plan</h3>
          {s.plan.rows.length ? (
            <table className="w-full text-sm" data-testid="present-plan-table"><tbody>
              {s.plan.rows.map((x) => <tr key={x.id} className="border-b border-slate-100"><td className="py-1.5 text-slate-600">{x.need} · {x.person}</td><td className="py-1.5 font-semibold">{x.product}</td><td className="py-1.5 text-right font-mono">{m(x.premium)}/mo</td></tr>)}
              <tr><td className="pt-2 font-bold" colSpan={2}>Total monthly investment</td><td className="pt-2 text-right font-mono font-bold text-[#0072C6]" data-testid="present-total-premium">{m(s.plan.totalPremium)}/mo</td></tr>
            </tbody></table>
          ) : (
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700">{recommendedActions(s, cur, money).map((a) => <li key={a}>{a}</li>)}</ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default function PresentPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [slide, setSlide] = useState(0);
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [sc, setSc] = useState(null);
  const [applying, setApplying] = useState(false);
  useEffect(() => { api.get(id).then((c) => { setClient(c); setSc(scenarioDefaults(migrate(c.data))); }).catch(() => nav("/clients")); }, [id, nav]);
  const baseData = useMemo(() => (client ? migrate(client.data) : null), [client]);
  const base = useMemo(() => (baseData ? computeSynopsis(baseData) : null), [baseData]);
  const changed = !!(sc && baseData && JSON.stringify(sc) !== JSON.stringify(scenarioDefaults(baseData)));
  const s = useMemo(() => (base && changed ? computeSynopsis(applyScenario(baseData, sc)) : base), [base, baseData, sc, changed]);
  const resetScenario = () => setSc(scenarioDefaults(baseData));
  const applyToClient = async () => {
    setApplying(true);
    try {
      const updated = await api.update(id, applyScenario(baseData, sc, `a${Date.now()}`));
      setClient(updated);
      setSc(scenarioDefaults(migrate(updated.data)));
      toast.success("Scenario saved to the client");
    } catch { toast.error("Could not save the scenario"); }
    setApplying(false);
  };
  const exit = useCallback(() => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); nav(`/clients/${id}`); }, [id, nav]);
  const fullscreen = () => document.documentElement.requestFullscreen?.().catch(() => {});

  useEffect(() => {
    fullscreen();
    const onKey = (e) => {
      if (e.target.closest?.("[role=slider]")) return;
      if (e.key === "ArrowRight") setSlide((x) => Math.min(SLIDES.length - 1, x + 1));
      if (e.key === "ArrowLeft") setSlide((x) => Math.max(0, x - 1));
      if (e.key === "Escape" && !document.fullscreenElement) exit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [exit]);

  if (!s) return <div className="present grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0072C6]" /></div>;
  const cur = s.d.profile.currency;
  const m = (v) => money(v, cur);
  const p = s.d.profile;
  const Slide = [Glance, Position, Retirement, Protection][slide];

  return (
    <div className="present" data-testid="meeting-mode">
      <div className="present-top">
        <div className="flex items-center gap-4">
          <img src="/experior-logo.svg" alt="Experior Financial Group" className="h-10" />
          <div className="leading-tight border-l border-slate-200 pl-4">
            <p className="font-heading font-extrabold text-slate-900" data-testid="present-client-name">{p.name}{p.spouse.name ? ` & ${p.spouse.name}` : ""}</p>
            <p className="text-xs text-slate-500">Financial Needs Analysis · {p.meetingDate}</p>
          </div>
        </div>
        <div className="present-dots">{SLIDES.map((t, i) => <button key={t} className={`present-dot ${i === slide ? "is-active" : ""}`} onClick={() => setSlide(i)} data-testid={`present-slide-${i}`}>{i + 1}. {t}</button>)}</div>
        <div className="flex gap-2">
          <Button variant={showWhatIf ? "default" : "outline"} size="sm" onClick={() => setShowWhatIf(!showWhatIf)} data-testid="whatif-toggle-button" className="gap-1.5"><SlidersHorizontal className="h-4 w-4" /> What-if</Button>
          <Button variant="outline" size="icon" onClick={fullscreen} data-testid="present-fullscreen-button" aria-label="Full screen"><Maximize className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={exit} data-testid="present-exit-button" className="gap-1.5"><X className="h-4 w-4" /> Exit</Button>
        </div>
      </div>
      <div className="present-main">
        <div className="present-body">
          <div className="flex items-center gap-3 mb-5">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900" data-testid="present-slide-title">{SLIDES[slide]}</h2>
            {changed && <span className="whatif-badge" data-testid="whatif-active-badge">What-if scenario · not saved</span>}
          </div>
          <div key={slide} className="slide-in"><Slide s={s} m={m} cur={cur} /></div>
        </div>
        {showWhatIf && <WhatIfPanel base={base} s={s} sc={sc} setSc={setSc} changed={changed} onReset={resetScenario} onApply={applyToClient} applying={applying} cur={cur} />}
      </div>
      <div className="flex justify-between px-10 pb-5">
        <Button variant="outline" disabled={slide === 0} onClick={() => setSlide(slide - 1)} data-testid="present-prev-button" className="gap-1.5"><ChevronLeft className="h-4 w-4" /> Previous</Button>
        <Button disabled={slide === SLIDES.length - 1} onClick={() => setSlide(slide + 1)} data-testid="present-next-button" className="gap-1.5">Next <ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
